import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Clock, Star, Mail, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";
import RecipeCard from "@/components/RecipeCard";
import EbookCTA from "@/components/EbookCTA";
import TrustBadges from "@/components/TrustBadges";
import BecomeChefSection from "@/components/BecomeChefSection";
import { HOME } from "@/constants/testIds";

const Stars = ({ rating, size = 14 }) => (
  <span className="inline-flex items-center gap-0.5 text-[color:var(--mango)]">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} fill={i <= Math.round(rating) ? "currentColor" : "none"} strokeWidth={1.5} />
    ))}
  </span>
);

const QUICK_FILTERS = [
  { key: "all", to: "/recipes" },
  { key: "easy", to: "/recipes?difficulty=easy" },
  { key: "quick", to: "/recipes?max_time=30" },
  { key: "soup", to: "/recipes?type=soup" },
  { key: "dessert", to: "/recipes?type=dessert" },
  { key: "classic", to: "/selections/classiques-haiti" },
  { key: "veggie", to: "/recipes?q=veggie" },
];

function SectionHeader({ num, title, subtitle, to, t }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
      <div>
        <p className="eyebrow mb-3 flex items-center gap-3">
          <span className="section-num not-italic">{num}</span>
          <span className="h-px w-10 bg-[color:var(--ink)] opacity-30" />
        </p>
        <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">{title}</h2>
        {subtitle && <p className="text-base text-[color:var(--ink-2)] max-w-2xl mt-3">{subtitle}</p>}
      </div>
      {to && (
        <Link to={to} className="text-xs tracking-[0.22em] uppercase font-medium underline underline-offset-8 decoration-[color:var(--red)] hover:text-[color:var(--navy)]">
          {t.sectionSeeAll} →
        </Link>
      )}
    </div>
  );
}

function Newsletter() {
  const { lang } = useLang();
  const t = useDict(lang);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | ok | err

  const submit = async (e) => {
    e.preventDefault();
    setState("idle");
    try {
      await api.subscribeNewsletter(email);
      setState("ok");
      setEmail("");
    } catch {
      setState("err");
    }
  };

  return (
    <section className="bg-[color:var(--navy)] text-[color:var(--bg)] mt-32 py-24">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <p className="text-xs tracking-[0.28em] uppercase opacity-70 mb-4">{t.newsletter.eyebrow}</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.05]">{t.newsletter.title}</h2>
        </div>
        <form onSubmit={submit} className="lg:col-span-5 space-y-4">
          <div className="flex items-center gap-3 border-b border-[color:var(--bg)] py-3">
            <Mail size={18} className="opacity-70" />
            <input
              data-testid="newsletter-email"
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.newsletter.placeholder}
              className="flex-1 bg-transparent outline-none placeholder:text-white/60 text-white"
            />
            <button
              data-testid="newsletter-subscribe"
              type="submit"
              className="text-xs tracking-[0.22em] uppercase font-medium hover:opacity-80 transition-opacity inline-flex items-center gap-2"
            >
              {state === "ok" ? <><Check size={14} /> {t.newsletter.success}</> : <>{t.newsletter.subscribe} <ArrowRight size={14} /></>}
            </button>
          </div>
          <p className="text-xs opacity-60 max-w-md">{t.newsletter.consent}</p>
          {state === "err" && <p className="text-xs text-[color:var(--mango)]">{t.newsletter.error}</p>}
        </form>
      </div>
    </section>
  );
}

export default function HomePage() {
  const { lang } = useLang();
  const t = useDict(lang);
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [selections, setSelections] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.featuredRecipes().then(setFeatured).catch(() => {});
    api.listSelections().then(setSelections).catch(() => {});
    api.testimonials().then(setTestimonials).catch(() => {});
  }, []);

  const tonightPick = featured[0];
  const recentGrid = featured.slice(1, 5);

  return (
    <div>
      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-12 lg:pt-20 pb-6">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          <div className="lg:col-span-7 fade-in-up">
            <p className="eyebrow mb-6">{t.home.eyebrow}</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-[6.5rem] leading-[0.95] tracking-tight">
              {lang === "fr" ? (
                <>Les meilleures recettes<br />de la cuisine <em className="font-display-italic text-[color:var(--red)]">caribéenne</em>.</>
              ) : (
                <>The best recipes<br />of the <em className="font-display-italic text-[color:var(--red)]">Caribbean</em> kitchen.</>
              )}
            </h1>
            <p className="mt-8 text-lg text-[color:var(--ink-2)] max-w-xl leading-relaxed">{t.home.heroSub}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/recipes" data-testid={HOME.heroCta} className="btn-navy">{t.home.heroCta} <ArrowRight size={14} /></Link>
              <Link to="/shop" data-testid={HOME.shopCta} className="btn-outline">{t.home.shopCta}</Link>
            </div>
          </div>
          <div className="lg:col-span-5 fade-in-up">
            <div className="aspect-[4/5] overflow-hidden bg-[var(--bg-2)]">
              {tonightPick && (
                <img src={tonightPick.image} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            {tonightPick && (
              <p className="eyebrow mt-4 text-right">{tonightPick.country} · {tonightPick.title[lang] || tonightPick.title.fr}</p>
            )}
          </div>
        </div>
      </section>

      {/* QUICK FILTERS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-10">
        <div className="flex items-center gap-3 overflow-x-auto pb-2 no-scrollbar">
          {QUICK_FILTERS.map((f) => (
            <Link key={f.key} to={f.to} data-testid={`quick-${f.key}`} className="country-chip whitespace-nowrap">
              {t.quickFilters[f.key]}
            </Link>
          ))}
        </div>
      </section>

      {/* TRUST BADGES */}
      <div className="mt-12">
        <TrustBadges />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-16"><div className="divider-thin" /></div>

      {/* EBOOK CTA — primary lead magnet */}
      <EbookCTA />

      {/* TONIGHT PICK */}
      {tonightPick && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-20">
          <p className="text-xs tracking-[0.28em] uppercase text-[color:var(--red)] mb-6 font-medium">{t.tonight.eyebrow}</p>
          <Link to={`/recipes/${tonightPick.slug}`} className="grid lg:grid-cols-12 gap-10 lg:gap-16 group">
            <div className="lg:col-span-7 aspect-[16/10] overflow-hidden bg-[var(--bg-2)]">
              <img src={tonightPick.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="lg:col-span-5 lg:pt-10">
              <p className="eyebrow mb-4">{tonightPick.country} · {t.types[tonightPick.type] || tonightPick.type}</p>
              <h3 className="font-serif-display text-4xl sm:text-5xl leading-[1.05] mb-5 group-hover:text-[color:var(--navy)] transition-colors">{tonightPick.title[lang] || tonightPick.title.fr}</h3>
              <div className="flex items-center gap-3 text-sm mb-5">
                <Stars rating={tonightPick.rating || 4.8} />
                <span className="font-medium">{(tonightPick.rating || 4.8).toFixed(1)}</span>
                <span className="text-[color:var(--ink-3)]">· {tonightPick.reviews_count} {lang === 'fr' ? 'avis' : 'reviews'}</span>
              </div>
              <p className="text-base text-[color:var(--ink-2)] leading-relaxed mb-6">{tonightPick.summary[lang] || tonightPick.summary.fr}</p>
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-medium border-b border-[color:var(--ink)] pb-1">{t.tonight.cta} <ArrowRight size={14} /></span>
            </div>
          </Link>
        </section>
      )}

      {/* NOTRE SÉLECTION (mix featured) */}
      {recentGrid.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-28">
          <SectionHeader num="01" title={lang === 'fr' ? 'Notre sélection' : 'Our selection'} subtitle={lang === 'fr' ? 'Une poignée de plats à essayer cette semaine.' : 'A handful of dishes to try this week.'} to="/recipes" t={t} />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10 stagger">
            {recentGrid.map((r) => <RecipeCard key={r.slug} recipe={r} testId={HOME.recipeCard(r.slug)} compact />)}
          </div>
        </section>
      )}

      {/* THEMATIC SELECTIONS */}
      {selections.map((sel, idx) => (
        <section key={sel.slug} className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-28">
          <SectionHeader
            num={String(idx + 2).padStart(2, "0")}
            title={sel.title[lang] || sel.title.fr}
            subtitle={sel.subtitle[lang] || sel.subtitle.fr}
            to={`/selections/${sel.slug}`}
            t={t}
          />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 lg:gap-10 stagger">
            {sel.recipes.slice(0, 4).map((r) => (
              <RecipeCard key={r.slug} recipe={r} testId={`section-${sel.slug}-${r.slug}`} compact />
            ))}
          </div>
        </section>
      ))}

      {/* NEWSLETTER */}
      <Newsletter />

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-28">
          <SectionHeader num={String(selections.length + 2).padStart(2, "0")} title={t.testimonialsTitle} t={t} />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(0, 6).map((tm) => (
              <Link key={tm.recipe.slug} to={`/recipes/${tm.recipe.slug}`} className="group flex gap-4 border border-[color:rgba(0,16,48,0.12)] p-5 hover:border-[color:var(--ink)] transition-colors">
                {tm.recipe.image && (
                  <img src={tm.recipe.image} alt="" className="w-24 h-28 object-cover bg-[var(--bg-2)] flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="eyebrow truncate">{tm.recipe.title[lang] || tm.recipe.title.fr}</p>
                  <p className="text-sm text-[color:var(--ink)] mt-2 leading-relaxed italic">«&nbsp;{tm.text[lang] || tm.text.fr}&nbsp;»</p>
                  <div className="mt-3 flex items-center gap-2">
                    <Stars rating={tm.rating} size={12} />
                    <span className="text-xs text-[color:var(--ink-3)] uppercase tracking-[0.18em]">{tm.author}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* SHOP TEASER */}
      <section className="bg-[var(--bg-2)] mt-32 py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="eyebrow mb-3">{lang === 'fr' ? 'La boutique BAYO' : 'BAYO Shop'}</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-2 mb-6 leading-tight">{t.home.shopTitle}</h2>
            <p className="text-base text-[color:var(--ink-2)] max-w-md leading-relaxed mb-8">{t.home.shopSub}</p>
            <Link to="/shop" className="btn-navy">{t.home.shopCta} <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="/api/static/recipes/pikliz.png" className="aspect-[3/4] object-cover" alt="" />
            <img src="/api/static/recipes/piment-confit.png" className="aspect-[3/4] object-cover mt-12" alt="" />
          </div>
        </div>
      </section>

      {/* COMMUNITY · BECOME CHEF BAYO (gamification + leaderboard) */}
      <BecomeChefSection />
    </div>
  );
}
